import { HttpApiError, HttpApiGroup } from "@effect/platform";
import { Schema } from "effect";
import { endpoint } from "../auth/endpoint";
import { DatabaseError, DataIntegrityError } from "../auth/errors";
import { ShopperAiIntegrationId } from "../ids/definitions";

const Participant = Schema.Struct({
  id: Schema.String,
  name: Schema.optional(Schema.String),
});

const MetaConversationSummary = Schema.Struct({
  id: Schema.String,
  updatedTime: Schema.optional(Schema.String),
  messageCount: Schema.optional(Schema.Number),
  unreadCount: Schema.optional(Schema.Number),
  participants: Schema.Array(Participant),
});

const MetaMessageSummary = Schema.Struct({
  id: Schema.String,
  createdTime: Schema.String,
  from: Participant,
  to: Schema.optional(Schema.Array(Participant)),
  message: Schema.optional(Schema.String),
  attachmentCount: Schema.Number,
});

const RecentDmsResponse = Schema.Struct({
  conversations: Schema.Array(MetaConversationSummary),
});

const ConversationResponse = Schema.Struct({
  messages: Schema.Array(MetaMessageSummary),
});

const FacebookPostCommentSummary = Schema.Struct({
  id: Schema.String,
  createdTime: Schema.String,
  fromId: Schema.optional(Schema.String),
  fromName: Schema.optional(Schema.String),
  message: Schema.optional(Schema.String),
  commentCount: Schema.optional(Schema.Number),
  likeCount: Schema.optional(Schema.Number),
});

const FacebookPostCommentsResponse = Schema.Struct({
  postPermalink: Schema.optional(Schema.String),
  comments: Schema.Array(FacebookPostCommentSummary),
});

const InstagramMediaCommentSummary = Schema.Struct({
  id: Schema.String,
  timestamp: Schema.String,
  username: Schema.optional(Schema.String),
  text: Schema.optional(Schema.String),
  likeCount: Schema.optional(Schema.Number),
  replyCount: Schema.optional(Schema.Number),
});

const InstagramMediaCommentsResponse = Schema.Struct({
  mediaPermalink: Schema.optional(Schema.String),
  comments: Schema.Array(InstagramMediaCommentSummary),
});

const RawCommentResponse = Schema.Record({
  key: Schema.String,
  value: Schema.Unknown,
});

const MetaIntegrationSummary = Schema.Struct({
  id: ShopperAiIntegrationId,
  platform: Schema.Literal("facebook", "instagram"),
  pageId: Schema.String,
  pageName: Schema.String,
});

const MetaIntegrationsResponse = Schema.Struct({
  integrations: Schema.Array(MetaIntegrationSummary),
});

const IntegrationIdPath = Schema.Struct({ id: ShopperAiIntegrationId });

const ConversationParams = Schema.Struct({ conversationId: Schema.String });

const PostParams = Schema.Struct({ postId: Schema.String });

const MediaParams = Schema.Struct({ mediaId: Schema.String });

const CommentParams = Schema.Struct({ commentId: Schema.String });

export const IntegrationDevToolsGroup = HttpApiGroup.make("integrationDevTools")
  .add(
    endpoint.merchant
      .get("listMetaIntegrations", "/integrations/meta")
      .addSuccess(MetaIntegrationsResponse)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get("getFacebookRecentDms", "/integrations/:id/facebook/recent-dms")
      .setPath(IntegrationIdPath)
      .addSuccess(RecentDmsResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get("getFacebookConversation", "/integrations/:id/facebook/conversation")
      .setPath(IntegrationIdPath)
      .setUrlParams(ConversationParams)
      .addSuccess(ConversationResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get(
        "getFacebookPostComments",
        "/integrations/:id/facebook/post-comments",
      )
      .setPath(IntegrationIdPath)
      .setUrlParams(PostParams)
      .addSuccess(FacebookPostCommentsResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get("getFacebookComment", "/integrations/:id/facebook/comment")
      .setPath(IntegrationIdPath)
      .setUrlParams(CommentParams)
      .addSuccess(RawCommentResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get("getInstagramRecentDms", "/integrations/:id/instagram/recent-dms")
      .setPath(IntegrationIdPath)
      .addSuccess(RecentDmsResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get(
        "getInstagramConversation",
        "/integrations/:id/instagram/conversation",
      )
      .setPath(IntegrationIdPath)
      .setUrlParams(ConversationParams)
      .addSuccess(ConversationResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get(
        "getInstagramMediaComments",
        "/integrations/:id/instagram/media-comments",
      )
      .setPath(IntegrationIdPath)
      .setUrlParams(MediaParams)
      .addSuccess(InstagramMediaCommentsResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get("getInstagramComment", "/integrations/:id/instagram/comment")
      .setPath(IntegrationIdPath)
      .setUrlParams(CommentParams)
      .addSuccess(RawCommentResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .prefix("/api");
